import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { TabsTrigger } from '@ifixit/ui/components/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { isShortcutClaimed } from './shortcut_scope';

function subscribeToHash(onChange: () => void) {
   // pushState-driven back/forward can fire popstate without hashchange;
   // subscribing to both keeps the store honest either way.
   window.addEventListener('hashchange', onChange);
   window.addEventListener('popstate', onChange);
   return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
   };
}

/**
 * Tab selection backed by location.hash: the hash is the source of truth,
 * so #steps deep-links, every switch is a history entry (unless the caller
 * passes `replace`), and back/forward move between tabs for free. An empty or unknown hash falls back to
 * `fallback` (legacy step URLs land on Steps that way). A tab may append a
 * sub-route (#steps/123); only the first segment names the tab.
 *
 * The third return value is that sub-route, for a tab that needs an
 * argument in the URL (#prompts/de). Pass it to `setActiveTab` to write
 * one. Ignoring it is fine: appending it never changes which tab is
 * active.
 *
 * Pass `{ replace: true }` for moves that walk a document part by part,
 * so a long walk doesn't bury Back under one entry per part.
 *
 * A nonempty hash naming no tab leaves the current tab alone rather than
 * snapping back to `fallback`. The page chrome around these editors links
 * to #content, #menu and #search, and a skip-to-content link must not throw
 * away whatever the user had open. An empty hash still selects `fallback`
 * so Back can restore a page that originally loaded without a hash.
 */
export function useHashTab(
   validTabs: string[],
   fallback: string
): [
   string,
   (tab: string, subRoute?: string, options?: { replace?: boolean }) => void,
   string | null,
] {
   const hash = useSyncExternalStore(
      subscribeToHash,
      () => window.location.hash.slice(1),
      () => ''
   );
   const [hashTab, subRoute] = hash.split('/');
   const namesATab = validTabs.includes(hashTab);

   // Remember the last tab the hash actually named, so a hash that names
   // none of them can fall back to it. Adjusted during render rather than
   // in an effect: the next line reads it, and an effect would leave one
   // paint showing the wrong tab.
   const [lastNamedTab, setLastNamedTab] = useState(() => (namesATab ? hashTab : fallback));
   if (namesATab && hashTab !== lastNamedTab) {
      setLastNamedTab(hashTab);
   }

   const activeTab = namesATab ? hashTab : hash === '' ? fallback : lastNamedTab;
   const setActiveTab = (
      tab: string,
      nextSubRoute?: string,
      { replace = false }: { replace?: boolean } = {}
   ) => {
      // The history API instead of assigning location.hash: assignment
      // scrolls to any element whose id matches the tab name (server
      // templates emit bare-id divs). Neither call fires an event, so
      // notify the store's subscribers ourselves.
      const target = nextSubRoute === undefined ? tab : `${tab}/${nextSubRoute}`;
      if (replace) {
         window.history.replaceState(null, '', `#${target}`);
      } else {
         window.history.pushState(null, '', `#${target}`);
      }
      window.dispatchEvent(new HashChangeEvent('hashchange'));
   };
   return [activeTab, setActiveTab, namesATab ? (subRoute ?? null) : null];
}

/**
 * Tab selection backed by the `?tab=` search param, for editors whose hash
 * is spoken for (or unused): the wiki editors sync their active tab here so
 * Back after leaving the page restores it. The default tab keeps a clean
 * URL instead of writing `?tab=<fallback>`; replaceState keeps tab
 * switches out of the history stack.
 */
export function useSearchParamTab(
   validTabs: readonly string[],
   fallback: string
): [string, (tab: string) => void] {
   const [tab, setTab] = useState(() => {
      const param = new URLSearchParams(window.location.search).get('tab');
      return param !== null && validTabs.includes(param) ? param : fallback;
   });
   const setActiveTab = (value: string) => {
      if (!validTabs.includes(value)) {
         return;
      }
      setTab(value);
      const url = new URL(window.location.href);
      if (value === fallback) {
         url.searchParams.delete('tab');
      } else {
         url.searchParams.set('tab', value);
      }
      window.history.replaceState(null, '', url);
   };
   return [tab, setActiveTab];
}

/**
 * ←/→ walk the tabs, the sibling of the Steps tab's ↑/↓ shortcut: only
 * outside text inputs and modals, and only when nothing else consumed
 * the key (Radix's tablist and drag grips handle their own arrows).
 */
export function useArrowTabNav(
   tabOrder: string[],
   activeTab: string,
   setActiveTab: (tab: string) => void
): void {
   // The listener binds once; a ref keeps it reading current values so the
   // document subscription doesn't churn on every consumer render (the
   // editors re-render per keystroke).
   const stateRef = useRef({ tabOrder, activeTab, setActiveTab });
   useEffect(() => {
      stateRef.current = { tabOrder, activeTab, setActiveTab };
   });

   useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
         if (event.defaultPrevented) {
            return;
         }
         if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
            return;
         }
         if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return;
         }
         if (isShortcutClaimed(event)) {
            return;
         }
         const { tabOrder: tabs, activeTab: active, setActiveTab: setActive } = stateRef.current;
         const index = tabs.indexOf(active);
         const neighbor = tabs[event.key === 'ArrowRight' ? index + 1 : index - 1];
         if (neighbor) {
            setActive(neighbor);
            event.preventDefault();
         }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, []);
}

/**
 * The unsaved-changes marker on a tab trigger. Absolutely positioned in the
 * trigger's top-right corner (the trigger is `relative`) so it never adds
 * width or shifts the label — every tab keeps its natural size whether or not
 * it's dirty. Shown, it's a hover/focus tooltip with matching sr-only text.
 */
export function DirtyDot({ show }: { show: boolean }) {
   if (!show) {
      return null;
   }
   return (
      <Tooltip>
         <TooltipTrigger asChild>
            <span className="absolute right-0.5 top-1.5 inline-flex size-1.5 items-center justify-center">
               <span className="size-1.5 rounded-full bg-brand-500" />
               <span className="sr-only">{_js('Unsaved changes')}</span>
            </span>
         </TooltipTrigger>
         <TooltipContent>{_js('Unsaved changes')}</TooltipContent>
      </Tooltip>
   );
}

/**
 * One tab in a v2 editor's strip: an icon-and-label pill with a dirty dot,
 * matching the guide editor's tab strip.
 */
export function EditorTab({
   value,
   icon,
   dirty,
   children,
}: {
   value: string;
   icon?: string;
   dirty: boolean;
   children: ReactNode;
}) {
   return (
      <TabsTrigger
         value={value}
         data-testid={`editor-tab-${value}`}
         className="relative gap-1.5 px-2.5 py-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-foreground data-[state=active]:text-foreground"
      >
         {icon && <FaIcon name={icon} className="size-3.5" aria-hidden />}
         {children}
         <DirtyDot show={dirty} />
      </TabsTrigger>
   );
}
